import type { ELanguage } from '../languageHelper';

import { type ITextBase, oftenUsedTranslations } from './oftenUsed';

export interface IMessagesTexts extends ITextBase {
    // ComposeTab
    composeNewMessage: string;
    messageSentSuccessfully: string;
    fillAllFields: string;
    errorSendingMessage: string;
    to: string;
    selectRecipients: string;
    subject: string;
    enterSubject: string;
    message: string;
    yourMessage: string;
    saveDraft: string;
    send: string;
    sending: string;
    loadingEmployees: string;
    noEmployeesFound: string;

    // InboxTab
    loadingMessages: string;
    noMessages: string;
    newMessage: string;
    markAsRead: string;
    delete: string;
    filter: string;
    sort: string;
    all: string;
    unread: string;
    direct: string;
    system: string;
    notifications: string;
    agent: string;
    date: string;
    sender: string;
    reply: string;
    showSuggestions: string;
    manageCertificate: string;
    showProject: string;
    showResults: string;
    openDocumentation: string;
    editProfile: string;
    showAppointment: string;
    showDetails: string;
    daysAgo: string;
    hoursAgo: string;
    justNow: string;
    replyTo: string;
    close: string;
    replyFunctionNotImplemented: string;

    // SentTab
    loadingSentMessages: string;
    noSentMessages: string;

    // SystemTab
    loadingSystemMessages: string;
    noSystemMessages: string;
    important: string;
    low: string;
    new: string;
    notification: string;
}

type ILangData = {
    [lang in ELanguage]: IMessagesTexts;
};

export const messagesTexts: ILangData = {
    th: {
        oftenUsed: oftenUsedTranslations.th,
        composeNewMessage: 'เขียนข้อความใหม่',
        messageSentSuccessfully: 'ส่งข้อความเรียบร้อยแล้ว!',
        fillAllFields: 'กรุณากรอกข้อมูลให้ครบทุกช่อง',
        errorSendingMessage: 'เกิดข้อผิดพลาดในการส่งข้อความ',
        to: 'ถึง',
        selectRecipients: 'เลือกผู้รับ...',
        subject: 'หัวข้อ',
        enterSubject: 'ใส่หัวข้อ...',
        message: 'ข้อความ',
        yourMessage: 'ข้อความของคุณ...',
        saveDraft: 'บันทึกร่าง',
        send: 'ส่ง',
        sending: 'กำลังส่ง...',
        loadingEmployees: 'กำลังโหลดพนักงาน...',
        noEmployeesFound: 'ไม่พบพนักงาน',
        loadingMessages: 'กำลังโหลดข้อความ...',
        noMessages: 'ไม่มีข้อความ',
        newMessage: 'ข้อความใหม่',
        markAsRead: 'ทำเครื่องหมายว่าอ่านแล้ว',
        delete: 'ลบ',
        filter: 'กรอง',
        sort: 'เรียง',
        all: 'ทั้งหมด',
        unread: 'ยังไม่อ่าน',
        direct: 'โดยตรง',
        system: 'ระบบ',
        notifications: 'การแจ้งเตือน',
        agent: 'เอเจนต์',
        date: 'วันที่',
        sender: 'ผู้ส่ง',
        reply: 'ตอบกลับ',
        showSuggestions: 'แสดงคำแนะนำ',
        manageCertificate: 'จัดการใบรับรอง',
        showProject: 'แสดงโครงการ',
        showResults: 'แสดงผลลัพธ์',
        openDocumentation: 'เปิดเอกสาร',
        editProfile: 'แก้ไขโปรไฟล์',
        showAppointment: 'แสดงนัดหมาย',
        showDetails: 'แสดงรายละเอียด',
        daysAgo: 'วันที่แล้ว',
        hoursAgo: 'ชั่วโมงที่แล้ว',
        justNow: 'เมื่อสักครู่',
        replyTo: 'ตอบกลับ:',
        close: 'ปิด',
        replyFunctionNotImplemented: 'ฟังก์ชันนี้จะถูกพัฒนาในเวอร์ชันต่อไป',
        loadingSentMessages: 'กำลังโหลดข้อความที่ส่งแล้ว...',
        noSentMessages: 'ไม่มีข้อความที่ส่งแล้ว',
        loadingSystemMessages: 'กำลังโหลดข้อความระบบ...',
        noSystemMessages: 'ไม่มีข้อความระบบ',
        important: 'สำคัญ',
        low: 'ต่ำ',
        new: 'ใหม่',
        notification: 'การแจ้งเตือน',
    },
    en: {
        oftenUsed: oftenUsedTranslations.en,
        composeNewMessage: 'Compose New Message',
        messageSentSuccessfully: 'Message sent successfully!',
        fillAllFields: 'Please fill in all fields.',
        errorSendingMessage: 'Error sending message.',
        to: 'To',
        selectRecipients: 'Select recipients...',
        subject: 'Subject',
        enterSubject: 'Enter subject...',
        message: 'Message',
        yourMessage: 'Your message...',
        saveDraft: 'Save Draft',
        send: 'Send',
        sending: 'Sending...',
        loadingEmployees: 'Loading employees...',
        noEmployeesFound: 'No employees found',
        loadingMessages: 'Loading messages...',
        noMessages: 'No messages available',
        newMessage: 'New Message',
        markAsRead: 'Mark as Read',
        delete: 'Delete',
        filter: 'Filter',
        sort: 'Sort',
        all: 'All',
        unread: 'Unread',
        direct: 'Direct',
        system: 'System',
        notifications: 'Notifications',
        agent: 'Agent',
        date: 'Date',
        sender: 'Sender',
        reply: 'Reply',
        showSuggestions: 'Show Suggestions',
        manageCertificate: 'Manage Certificate',
        showProject: 'Show Project',
        showResults: 'Show Results',
        openDocumentation: 'Open Documentation',
        editProfile: 'Edit Profile',
        showAppointment: 'Show Appointment',
        showDetails: 'Show Details',
        daysAgo: 'days ago',
        hoursAgo: 'hours ago',
        justNow: 'Just now',
        replyTo: 'Reply to:',
        close: 'Close',
        replyFunctionNotImplemented: 'This function will be implemented in a future version.',
        loadingSentMessages: 'Loading sent messages...',
        noSentMessages: 'No sent messages',
        loadingSystemMessages: 'Loading system messages...',
        noSystemMessages: 'No system messages',
        important: 'Important',
        low: 'Low',
        new: 'New',
        notification: 'Notification',
    },
    de: {
        oftenUsed: oftenUsedTranslations.de,
        composeNewMessage: 'Neue Nachricht verfassen',
        messageSentSuccessfully: 'Nachricht wurde erfolgreich gesendet!',
        fillAllFields: 'Bitte füllen Sie alle Felder aus.',
        errorSendingMessage: 'Fehler beim Senden der Nachricht.',
        to: 'An',
        selectRecipients: 'Empfänger auswählen...',
        subject: 'Betreff',
        enterSubject: 'Betreff eingeben...',
        message: 'Nachricht',
        yourMessage: 'Ihre Nachricht...',
        saveDraft: 'Entwurf speichern',
        send: 'Senden',
        sending: 'Wird gesendet...',
        loadingEmployees: 'Lade Mitarbeiter...',
        noEmployeesFound: 'Keine Mitarbeiter gefunden',
        loadingMessages: 'Nachrichten werden geladen...',
        noMessages: 'Keine Nachrichten vorhanden',
        newMessage: 'Neue Nachricht',
        markAsRead: 'Als gelesen markieren',
        delete: 'Löschen',
        filter: 'Filter',
        sort: 'Sortieren',
        all: 'Alle',
        unread: 'Ungelesen',
        direct: 'Direkt',
        system: 'System',
        notifications: 'Benachrichtigungen',
        agent: 'Agent',
        date: 'Datum',
        sender: 'Absender',
        reply: 'Antworten',
        showSuggestions: 'Vorschläge anzeigen',
        manageCertificate: 'Zertifikat verwalten',
        showProject: 'Projekt anzeigen',
        showResults: 'Ergebnisse anzeigen',
        openDocumentation: 'Dokumentation öffnen',
        editProfile: 'Profil bearbeiten',
        showAppointment: 'Termin anzeigen',
        showDetails: 'Details anzeigen',
        daysAgo: 'Tage her',
        hoursAgo: 'Std. her',
        justNow: 'Gerade eben',
        replyTo: 'Antwort auf:',
        close: 'Schließen',
        replyFunctionNotImplemented: 'Diese Funktion wird in einer zukünftigen Version implementiert.',
        loadingSentMessages: 'Gesendete Nachrichten werden geladen...',
        noSentMessages: 'Keine gesendeten Nachrichten',
        loadingSystemMessages: 'System-Nachrichten werden geladen...',
        noSystemMessages: 'Keine System-Nachrichten',
        important: 'Wichtig',
        low: 'Niedrig',
        new: 'Neu',
        notification: 'Benachrichtigung',
    },
};
